import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  applyTaskUpdate,
  Difficulty,
  DIFFICULTIES,
  Language,
  LANGUAGES,
  Task,
  taskSchema,
  TASK_TYPES,
  TaskType,
  updateTaskRequestSchema,
  UpdateTaskRequest,
} from '@quizdb/common';
import { Icon } from '../common/Icon';
import { MaterialPageLink } from './MaterialPageLink';
import { TaskCheckCard } from './TaskCheckCard';
import { TaskFormProps } from './taskComponentProps';

/** The smallest and largest number of choice/item rows the form allows (QZ-D14). */
const MIN_ROWS = 2;
const MAX_ROWS = 6;

/** A task has one to three tags (QZ-D15). */
const MAX_TAGS = 3;

/** The form's local draft of a task's editable fields (QZ-D42); answer fields hold input strings. */
interface TaskDraft {
  taskTypes: TaskType[];
  text: string;
  choices: string[];
  freetextAnswer: string;
  numericAnswer: string;
  items: string[];
  difficulty: Difficulty;
  tags: string[];
  languageLimit: Language[];
  sourceUrl: string;
  sourceDocument: string;
  sourcePage: string;
  explanation: string;
}

type RowKey = 'choices' | 'items';

/** The draft initialised from `task`'s original-language translation (QZ-D41). */
const draftFrom = (task: Task): TaskDraft => {
  const original = task.translations[task.originalLanguage];
  return {
    taskTypes: [...task.taskTypes],
    text: original?.text ?? '',
    choices: original?.choices ?? [],
    freetextAnswer: original?.freetextAnswer ?? '',
    numericAnswer: task.numericAnswer !== undefined ? String(task.numericAnswer) : '',
    items: original?.items ?? [],
    difficulty: task.difficulty,
    tags: [...task.tags],
    languageLimit: task.languageLimit ?? [],
    sourceUrl: task.sourceReference.url ?? '',
    sourceDocument: task.sourceReference.document ?? '',
    sourcePage: task.sourceReference.page !== undefined ? String(task.sourceReference.page) : '',
    explanation: task.explanation,
  };
};

/** A blank string becomes `undefined`; anything else becomes a `number` (QZ-D42). */
const toNumber = (value: string): number | undefined =>
  value.trim() === '' ? undefined : Number(value);

/** The request built from a draft (QZ-D42, QZ-D41). Trimming happens here, never in the draft. */
const buildRequest = (draft: TaskDraft, task: Task): UpdateTaskRequest => {
  const hasType = (type: TaskType) => draft.taskTypes.includes(type);
  const url = draft.sourceUrl.trim();
  const document = draft.sourceDocument.trim();
  const page = toNumber(draft.sourcePage);
  const numericAnswer = toNumber(draft.numericAnswer);

  return {
    expectedUpdatedAt: task.updatedAt,
    taskTypes: TASK_TYPES.filter(hasType),
    difficulty: draft.difficulty,
    tags: [...draft.tags].sort(),
    ...(draft.languageLimit.length > 0
      ? { languageLimit: LANGUAGES.filter((language) => draft.languageLimit.includes(language)) }
      : {}),
    ...(hasType('NumericTiebreakerQuestion') && numericAnswer !== undefined
      ? { numericAnswer }
      : {}),
    sourceReference: {
      ...(url !== '' ? { url } : {}),
      ...(document !== '' ? { document } : {}),
      ...(page !== undefined ? { page } : {}),
    },
    explanation: draft.explanation.trim(),
    original: {
      text: draft.text.trim(),
      ...(hasType('MultiChoiceQuestion')
        ? { choices: draft.choices.map((choice) => choice.trim()) }
        : {}),
      ...(hasType('FreetextQuestion') ? { freetextAnswer: draft.freetextAnswer.trim() } : {}),
      ...(hasType('ItemOrdererer') ? { items: draft.items.map((item) => item.trim()) } : {}),
    },
  };
};

/** The shape a zod issue needs to have for `mapIssues`; `ZodIssue` satisfies this. */
interface Issue {
  path: readonly PropertyKey[];
  message: string;
}

/** Field keys the form shows an issue's message under; everything else is unplaced. */
interface MappedIssues {
  fields: Record<string, string[]>;
  unplaced: string[];
}

/**
 * Maps `taskSchema`'s issue paths, relative to the task, to the form's field ids (QZ-D42). A
 * path into a language other than the original is shown unplaced, prefixed with that language's
 * name; anything else unrecognised is unplaced too.
 */
const mapIssues = (
  issues: readonly Issue[],
  originalLanguage: Language,
  t: (key: string) => string
): MappedIssues => {
  const fields: Record<string, string[]> = {};
  const unplaced: string[] = [];

  const addField = (key: string, message: string) => {
    const list = fields[key] ?? [];
    if (!list.includes(message)) {
      fields[key] = [...list, message];
    }
  };
  const addUnplaced = (message: string) => {
    if (!unplaced.includes(message)) {
      unplaced.push(message);
    }
  };

  for (const issue of issues) {
    const [p0, p1, p2, p3] = issue.path;
    if (p0 === 'taskTypes') {
      addField('taskTypes', issue.message);
    } else if (p0 === 'difficulty') {
      addField('difficulty', issue.message);
    } else if (p0 === 'tags') {
      addField('tags', issue.message);
    } else if (p0 === 'languageLimit') {
      addField('languageLimit', issue.message);
    } else if (p0 === 'numericAnswer') {
      addField('numericAnswer', issue.message);
    } else if (p0 === 'explanation') {
      addField('explanation', issue.message);
    } else if (p0 === 'sourceReference' && p1 === 'url') {
      addField('sourceUrl', issue.message);
    } else if (p0 === 'sourceReference' && p1 === 'document') {
      addField('sourceDocument', issue.message);
    } else if (p0 === 'sourceReference' && p1 === 'page') {
      addField('sourcePage', issue.message);
    } else if (p0 === 'translations' && p1 === originalLanguage) {
      if (p2 === 'text') {
        addField('text', issue.message);
      } else if (p2 === 'freetextAnswer') {
        addField('freetextAnswer', issue.message);
      } else if (p2 === 'choices') {
        addField(typeof p3 === 'number' ? `choice.${p3}` : 'choices', issue.message);
      } else if (p2 === 'items') {
        addField(typeof p3 === 'number' ? `item.${p3}` : 'items', issue.message);
      } else {
        addUnplaced(issue.message);
      }
    } else if (p0 === 'translations' && typeof p1 === 'string') {
      addUnplaced(`${t(`task.language.${p1}`)}: ${issue.message}`);
    } else {
      addUnplaced(issue.message);
    }
  }

  return { fields, unplaced };
};

/** A field's issues, shown under it (QZ-D42). */
const FieldError = ({ id, messages }: { id: string; messages?: string[] }) => {
  if (!messages || messages.length === 0) {
    return null;
  }
  return (
    <div id={`${id}-error`} className="invalid-feedback d-block">
      {messages.map((message, index) => (
        <div key={index}>{message}</div>
      ))}
    </div>
  );
};

/**
 * A controlled form over a local draft of `props.task`'s editable fields (QZ-D41, QZ-D42),
 * laid out as the draft review page (QZ-D47): the question and its answers on the left, the
 * review fields on the right, and a sticky bar with Discard and the parent's `actions`. The
 * form never saves by itself; the parent's actions save its `request` (Approve saves, then
 * approves). The parent remounts it with `key={task.updatedAt}` after a save, so the draft is
 * initialised once from props and never synced back when they change.
 */
export const TaskForm = (props: TaskFormProps) => {
  const { task, tags, onDirtyChange, actions, children } = props;
  const { t } = useTranslation();

  const [draft, setDraft] = useState<TaskDraft>(() => draftFrom(task));
  const [initialJson] = useState(() => JSON.stringify(buildRequest(draftFrom(task), task)));
  const [pendingTag, setPendingTag] = useState('');

  const request = useMemo(() => buildRequest(draft, task), [draft, task]);
  const dirty = JSON.stringify(request) !== initialJson;

  const { fields, unplaced, valid } = useMemo(() => {
    const candidate = applyTaskUpdate(task, request, task.updatedAt);
    const result = taskSchema.safeParse(candidate);
    const requestResult = updateTaskRequestSchema.safeParse(request);
    const mapped = mapIssues(result.success ? [] : result.error.issues, task.originalLanguage, t);
    if (result.success && !requestResult.success) {
      for (const issue of requestResult.error.issues) {
        if (!mapped.unplaced.includes(issue.message)) {
          mapped.unplaced.push(issue.message);
        }
      }
    }
    return {
      fields: mapped.fields,
      unplaced: mapped.unplaced,
      valid: result.success && requestResult.success,
    };
  }, [request, task, t]);

  const reported = useRef(false);
  const latestOnDirtyChange = useRef(onDirtyChange);
  latestOnDirtyChange.current = onDirtyChange;
  useEffect(() => {
    if (dirty !== reported.current) {
      reported.current = dirty;
      latestOnDirtyChange.current(dirty);
    }
  }, [dirty]);
  useEffect(
    () => () => {
      if (reported.current) {
        latestOnDirtyChange.current(false);
      }
    },
    []
  );

  // The page shown by MaterialPageLink below: the draft's current value, so a corrected page
  // opens the corrected page (PDG-D11).
  const sourcePageNumber = Number(draft.sourcePage.trim());
  const showMaterialPageLink =
    draft.sourceDocument.trim() !== '' &&
    draft.sourcePage.trim() !== '' &&
    Number.isInteger(sourcePageNumber) &&
    sourcePageNumber >= 1;

  const handleTaskTypeChange = (type: TaskType, checked: boolean) => {
    setDraft((prev) => {
      const taskTypes = checked
        ? [...prev.taskTypes, type]
        : prev.taskTypes.filter((t2) => t2 !== type);
      if (checked && type === 'MultiChoiceQuestion' && prev.choices.length === 0) {
        return { ...prev, taskTypes, choices: ['', ''] };
      }
      if (checked && type === 'ItemOrdererer' && prev.items.length === 0) {
        return { ...prev, taskTypes, items: ['', ''] };
      }
      return { ...prev, taskTypes };
    });
  };

  const updateRow = (key: RowKey, index: number, value: string) => {
    setDraft((prev) => ({
      ...prev,
      [key]: prev[key].map((row, i) => (i === index ? value : row)),
    }));
  };

  const addRow = (key: RowKey) => {
    setDraft((prev) => ({ ...prev, [key]: [...prev[key], ''] }));
  };

  const removeRow = (key: RowKey, index: number) => {
    setDraft((prev) => ({ ...prev, [key]: prev[key].filter((_, i) => i !== index) }));
  };

  const moveRow = (key: RowKey, index: number, direction: -1 | 1) => {
    setDraft((prev) => {
      const rows = [...prev[key]];
      const target = index + direction;
      [rows[index], rows[target]] = [rows[target], rows[index]];
      return { ...prev, [key]: rows };
    });
  };

  const addTag = (tag: string) => {
    if (tag === '') {
      return;
    }
    setDraft((prev) =>
      prev.tags.length >= MAX_TAGS || prev.tags.includes(tag)
        ? prev
        : { ...prev, tags: [...prev.tags, tag] }
    );
    setPendingTag('');
  };

  const removeTag = (tag: string) => {
    setDraft((prev) => ({ ...prev, tags: prev.tags.filter((name) => name !== tag) }));
  };

  const toggleLanguage = (language: Language, checked: boolean) => {
    setDraft((prev) => ({
      ...prev,
      languageLimit: checked
        ? [...prev.languageLimit, language]
        : prev.languageLimit.filter((l) => l !== language),
    }));
  };

  /** Back to the task as loaded, as the parent's remount after a save would (QZ-D41). */
  const discard = () => {
    setDraft(draftFrom(task));
    setPendingTag('');
  };

  const tagOptions = useMemo(
    () => [...tags, ...task.tags.filter((tag) => !tags.includes(tag))],
    [tags, task.tags]
  );
  // The picker offers only tags not yet chosen, and closes at three (QZ-D15).
  const addableTags = tagOptions.filter((tag) => !draft.tags.includes(tag));
  const tagsFull = draft.tags.length >= MAX_TAGS;
  const hasType = (type: TaskType) => draft.taskTypes.includes(type);

  // There is no Save button, so Enter in a field must not submit anything, least of all an
  // approval.
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="row g-4">
        <div className="col-lg-8">
          <div className="qz-card">
            <div className="qz-card-body">
              <label htmlFor="task-text" className="form-label">
                {t('task.field.question')}
              </label>
              <textarea
                id="task-text"
                rows={4}
                className={`form-control qz-question-input${fields.text ? ' is-invalid' : ''}`}
                aria-describedby={fields.text ? 'task-text-error' : undefined}
                value={draft.text}
                onChange={(e) => setDraft((prev) => ({ ...prev, text: e.target.value }))}
              />
              <FieldError id="task-text" messages={fields.text} />

              <fieldset className="mt-4">
                <legend className="form-label">{t('task.field.playedAs')}</legend>
                <div className="qz-chips">
                  {TASK_TYPES.map((type) => (
                    <span className="qz-chip" key={type}>
                      <input
                        id={`task-type-${type}`}
                        type="checkbox"
                        checked={hasType(type)}
                        onChange={(e) => handleTaskTypeChange(type, e.target.checked)}
                      />
                      <label htmlFor={`task-type-${type}`}>{t(`task.type.${type}`)}</label>
                    </span>
                  ))}
                </div>
                <div className="form-text">{t('task.form.typesHint')}</div>
                <FieldError id="task-taskTypes" messages={fields.taskTypes} />
              </fieldset>
            </div>
          </div>

          {hasType('MultiChoiceQuestion') && (
            <fieldset className="qz-card" id="task-choices">
              <div className="qz-card-header">
                <legend className="h3 mb-0">{t('task.type.MultiChoiceQuestion')}</legend>
                <span className="qz-small qz-muted">{t('task.form.firstIsCorrect')}</span>
              </div>
              <div className="qz-card-body">
                {draft.choices.map((choice, index) => {
                  const rowErrors = fields[`choice.${index}`];
                  return (
                    <div key={index}>
                      <div className={`qz-answer-row${index === 0 ? ' is-correct' : ''}`}>
                        <span className="qz-answer-marker" aria-hidden="true">
                          {index === 0 ? <Icon name="check" /> : String.fromCharCode(65 + index)}
                        </span>
                        <input
                          id={`task-choice-${index}`}
                          type="text"
                          className={`form-control${rowErrors ? ' is-invalid' : ''}`}
                          aria-label={
                            index === 0
                              ? t('task.field.correctChoice')
                              : `${t('task.field.choices')} ${index + 1}`
                          }
                          aria-describedby={rowErrors ? `task-choice-${index}-error` : undefined}
                          value={choice}
                          onChange={(e) => updateRow('choices', index, e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-icon"
                          aria-label={t('task.form.remove')}
                          disabled={draft.choices.length <= MIN_ROWS}
                          onClick={() => removeRow('choices', index)}
                        >
                          <Icon name="xmark" />
                        </button>
                      </div>
                      <FieldError id={`task-choice-${index}`} messages={rowErrors} />
                    </div>
                  );
                })}
                <FieldError id="task-choices" messages={fields.choices} />
                <button
                  type="button"
                  className="btn btn-link btn-sm px-0"
                  disabled={draft.choices.length >= MAX_ROWS}
                  onClick={() => addRow('choices')}
                >
                  <Icon name="plus" /> {t('task.form.addChoice')}
                </button>
              </div>
            </fieldset>
          )}

          {hasType('FreetextQuestion') && (
            <div className="qz-card">
              <div className="qz-card-header">
                <h3>{t('task.type.FreetextQuestion')}</h3>
              </div>
              <div className="qz-card-body">
                <label htmlFor="task-freetextAnswer" className="form-label">
                  {t('task.field.freetextAnswer')}
                </label>
                <input
                  id="task-freetextAnswer"
                  type="text"
                  className={`form-control${fields.freetextAnswer ? ' is-invalid' : ''}`}
                  aria-describedby={fields.freetextAnswer ? 'task-freetextAnswer-error' : undefined}
                  value={draft.freetextAnswer}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, freetextAnswer: e.target.value }))
                  }
                />
                <FieldError id="task-freetextAnswer" messages={fields.freetextAnswer} />
              </div>
            </div>
          )}

          {hasType('NumericTiebreakerQuestion') && (
            <div className="qz-card">
              <div className="qz-card-header">
                <h3>{t('task.type.NumericTiebreakerQuestion')}</h3>
              </div>
              <div className="qz-card-body">
                <label htmlFor="task-numericAnswer" className="form-label">
                  {t('task.field.numericAnswer')}
                </label>
                <input
                  id="task-numericAnswer"
                  type="number"
                  step="any"
                  style={{ maxWidth: '200px' }}
                  className={`form-control${fields.numericAnswer ? ' is-invalid' : ''}`}
                  aria-describedby={fields.numericAnswer ? 'task-numericAnswer-error' : undefined}
                  value={draft.numericAnswer}
                  onChange={(e) => setDraft((prev) => ({ ...prev, numericAnswer: e.target.value }))}
                />
                <FieldError id="task-numericAnswer" messages={fields.numericAnswer} />
              </div>
            </div>
          )}

          {hasType('ItemOrdererer') && (
            <fieldset className="qz-card" id="task-items">
              <div className="qz-card-header">
                <legend className="h3 mb-0">{t('task.type.ItemOrdererer')}</legend>
                <span className="qz-small qz-muted">{t('task.form.correctOrder')}</span>
              </div>
              <div className="qz-card-body">
                {draft.items.map((item, index) => {
                  const rowErrors = fields[`item.${index}`];
                  return (
                    <div key={index}>
                      <div className="qz-answer-row">
                        <span className="qz-answer-marker" aria-hidden="true">
                          {index + 1}
                        </span>
                        <input
                          id={`task-item-${index}`}
                          type="text"
                          className={`form-control${rowErrors ? ' is-invalid' : ''}`}
                          aria-label={`${t('task.field.items')} ${index + 1}`}
                          aria-describedby={rowErrors ? `task-item-${index}-error` : undefined}
                          value={item}
                          onChange={(e) => updateRow('items', index, e.target.value)}
                        />
                        <div className="btn-group">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-icon"
                            aria-label={t('task.form.moveUp')}
                            disabled={index === 0}
                            onClick={() => moveRow('items', index, -1)}
                          >
                            <Icon name="arrow-up" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-icon"
                            aria-label={t('task.form.moveDown')}
                            disabled={index === draft.items.length - 1}
                            onClick={() => moveRow('items', index, 1)}
                          >
                            <Icon name="arrow-down" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-icon"
                            aria-label={t('task.form.remove')}
                            disabled={draft.items.length <= MIN_ROWS}
                            onClick={() => removeRow('items', index)}
                          >
                            <Icon name="xmark" />
                          </button>
                        </div>
                      </div>
                      <FieldError id={`task-item-${index}`} messages={rowErrors} />
                    </div>
                  );
                })}
                <FieldError id="task-items" messages={fields.items} />
                <button
                  type="button"
                  className="btn btn-link btn-sm px-0"
                  disabled={draft.items.length >= MAX_ROWS}
                  onClick={() => addRow('items')}
                >
                  <Icon name="plus" /> {t('task.form.addItem')}
                </button>
              </div>
            </fieldset>
          )}

          {unplaced.length > 0 && (
            <div className="alert alert-danger mt-3">
              <Icon name="circle-exclamation" />
              <ul className="mb-0">
                {unplaced.map((message, index) => (
                  <li key={index}>{message}</li>
                ))}
              </ul>
            </div>
          )}

          {children}
        </div>

        <div className="col-lg-4">
          <div className="qz-card">
            <div className="qz-card-body qz-stack">
              <fieldset>
                <legend className="form-label">{t('task.field.difficulty')}</legend>
                <div className="qz-segmented is-block">
                  {DIFFICULTIES.map((difficulty) => (
                    <span key={difficulty} className="d-flex flex-fill">
                      <input
                        id={`task-difficulty-${difficulty}`}
                        type="radio"
                        name="task-difficulty"
                        checked={draft.difficulty === difficulty}
                        onChange={() => setDraft((prev) => ({ ...prev, difficulty }))}
                      />
                      <label className="flex-fill" htmlFor={`task-difficulty-${difficulty}`}>
                        {t(`task.difficulty.${difficulty}`)}
                      </label>
                    </span>
                  ))}
                </div>
                <div className="form-text">{t('task.form.difficultyHint')}</div>
                <FieldError id="task-difficulty" messages={fields.difficulty} />
              </fieldset>

              <fieldset id="task-tags">
                <div className="d-flex justify-content-between">
                  <legend className="form-label">{t('task.field.tags')}</legend>
                  <span className="qz-small qz-muted">
                    {t('task.form.tagCount', { count: draft.tags.length, max: MAX_TAGS })}
                  </span>
                </div>
                <div className="qz-tags mb-2">
                  {draft.tags.map((tag) => (
                    <span key={tag} className="qz-tag">
                      {tag}
                      <button
                        type="button"
                        className="qz-tag-x"
                        aria-label={t('task.form.removeTag', { tag })}
                        onClick={() => removeTag(tag)}
                      >
                        <Icon name="xmark" />
                      </button>
                    </span>
                  ))}
                </div>
                {/*
                  Picking only chooses; the button adds. Arrowing through a closed select fires
                  `change` on each key in Chrome and Firefox on Windows, so adding on change
                  would add a tag per keypress.
                */}
                <div className="input-group input-group-sm">
                  <select
                    className="form-select"
                    aria-label={t('task.form.pickTag')}
                    value={pendingTag}
                    disabled={tagsFull || addableTags.length === 0}
                    onChange={(e) => setPendingTag(e.target.value)}
                  >
                    <option value="">
                      {tagsFull ? t('task.form.tooManyTags') : t('task.form.addTagPlaceholder')}
                    </option>
                    {addableTags.map((tag) => (
                      <option key={tag} value={tag}>
                        {tag}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={tagsFull || pendingTag === ''}
                    onClick={() => addTag(pendingTag)}
                  >
                    <Icon name="plus" /> {t('task.form.addTag')}
                  </button>
                </div>
                <FieldError id="task-tags" messages={fields.tags} />
              </fieldset>

              <fieldset id="task-languageLimit">
                <legend className="form-label">{t('task.field.languageLimit')}</legend>
                <div className="qz-chips">
                  {LANGUAGES.map((language) => (
                    <span className="qz-chip" key={language}>
                      <input
                        id={`task-languageLimit-${language}`}
                        type="checkbox"
                        checked={draft.languageLimit.includes(language)}
                        onChange={(e) => toggleLanguage(language, e.target.checked)}
                      />
                      <label
                        htmlFor={`task-languageLimit-${language}`}
                        title={t(`task.language.${language}`)}
                      >
                        <span aria-hidden="true">{language.toUpperCase()}</span>
                        <span className="visually-hidden">{t(`task.language.${language}`)}</span>
                      </label>
                    </span>
                  ))}
                </div>
                <div className="form-text">{t('task.form.languageLimitHint')}</div>
                <FieldError id="task-languageLimit" messages={fields.languageLimit} />
              </fieldset>
            </div>
          </div>

          <div className="qz-card">
            <div className="qz-card-header">
              <h3>{t('task.form.source')}</h3>
              {draft.sourceUrl.trim() !== '' && !fields.sourceUrl && (
                <a href={draft.sourceUrl} target="_blank" rel="noreferrer" className="qz-small">
                  {t('task.form.openSource')} <Icon name="arrow-up-right-from-square" />
                </a>
              )}
            </div>
            <div className="qz-card-body qz-stack">
              <div>
                <label htmlFor="task-sourceUrl" className="form-label">
                  {t('task.field.sourceUrl')}
                </label>
                <input
                  id="task-sourceUrl"
                  type="url"
                  className={`form-control form-control-sm${fields.sourceUrl ? ' is-invalid' : ''}`}
                  aria-describedby={fields.sourceUrl ? 'task-sourceUrl-error' : undefined}
                  value={draft.sourceUrl}
                  onChange={(e) => setDraft((prev) => ({ ...prev, sourceUrl: e.target.value }))}
                />
                <FieldError id="task-sourceUrl" messages={fields.sourceUrl} />
              </div>
              {showMaterialPageLink && (
                <div className="qz-small">
                  <Icon name="file-pdf" />{' '}
                  <MaterialPageLink jobId={task.jobId} page={sourcePageNumber} />
                </div>
              )}
              <div className="row g-2">
                <div className="col-8">
                  <label htmlFor="task-sourceDocument" className="form-label">
                    {t('task.field.sourceDocument')}
                  </label>
                  <input
                    id="task-sourceDocument"
                    type="text"
                    className={`form-control form-control-sm${fields.sourceDocument ? ' is-invalid' : ''}`}
                    aria-describedby={
                      fields.sourceDocument ? 'task-sourceDocument-error' : undefined
                    }
                    value={draft.sourceDocument}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, sourceDocument: e.target.value }))
                    }
                  />
                  <FieldError id="task-sourceDocument" messages={fields.sourceDocument} />
                </div>
                <div className="col-4">
                  <label htmlFor="task-sourcePage" className="form-label">
                    {t('task.field.sourcePage')}
                  </label>
                  <input
                    id="task-sourcePage"
                    type="number"
                    step="1"
                    className={`form-control form-control-sm${fields.sourcePage ? ' is-invalid' : ''}`}
                    aria-describedby={fields.sourcePage ? 'task-sourcePage-error' : undefined}
                    value={draft.sourcePage}
                    onChange={(e) => setDraft((prev) => ({ ...prev, sourcePage: e.target.value }))}
                  />
                  <FieldError id="task-sourcePage" messages={fields.sourcePage} />
                </div>
              </div>
            </div>
          </div>

          <div className="qz-card">
            <div className="qz-card-header">
              <h3>
                <label htmlFor="task-explanation" className="mb-0">
                  {t('task.field.explanation')}
                </label>
              </h3>
              <span className="qz-small qz-muted">{t('task.form.forReviewers')}</span>
            </div>
            <div className="qz-card-body">
              <textarea
                id="task-explanation"
                rows={4}
                className={`form-control form-control-sm${fields.explanation ? ' is-invalid' : ''}`}
                aria-describedby={fields.explanation ? 'task-explanation-error' : undefined}
                value={draft.explanation}
                onChange={(e) => setDraft((prev) => ({ ...prev, explanation: e.target.value }))}
              />
              <div className="form-text">{t('task.form.explanationHint')}</div>
              <FieldError id="task-explanation" messages={fields.explanation} />
            </div>
          </div>

          {task.check && <TaskCheckCard check={task.check} />}
        </div>
      </div>

      <div className="qz-actionbar">
        <div className="qz-container">
          {dirty && !valid && (
            <span className="qz-small text-danger">
              <Icon name="circle-exclamation" /> {t('task.form.fixFields')}
            </span>
          )}
          {dirty && valid && <span className="qz-dirty">{t('task.form.unsaved')}</span>}
          {!dirty && (
            <span className="qz-small qz-muted">
              <Icon name="circle-check" /> {t('task.form.saved')}
            </span>
          )}
          <span className="qz-spacer" />
          {dirty && (
            <button type="button" className="btn btn-outline-secondary" onClick={discard}>
              {t('task.form.discard')}
            </button>
          )}
          {actions?.({ dirty, valid, request })}
        </div>
      </div>
    </form>
  );
};

export default TaskForm;
